import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PhotoUploadService, UploadProgress } from '../../../services/photo-upload.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-upload-progress-badge',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (progress.isUploading) {
    <div class="upload-progress-badge">
      <div class="badge-content">
        <i class="pi pi-upload text-white"></i>
        <div class="upload-text">
          <div class="font-semibold">Uploading Photos...</div>
          <div class="text-sm">
            {{ progress.uploadedPhotos }} of {{ progress.totalPhotos }} completed
            @if (progress.failedPhotos > 0) {
              <span class="text-red-300">({{ progress.failedPhotos }} failed)</span>
            }
          </div>
        </div>
        <i class="pi pi-spin pi-spinner text-white ml-2"></i>
      </div>
    </div>
    }

    @if (!progress.isUploading && progress.uploadedPhotos > 0 && showCompletedMessage) {
    <div class="upload-progress-badge completed" (click)="dismissCompleted()">
      <div class="badge-content">
        <i class="pi pi-check-circle text-white"></i>
        <div class="upload-text">
          <div class="font-semibold">Upload Complete!</div>
          <div class="text-sm">
            {{ progress.uploadedPhotos }} photo(s) uploaded successfully
            @if (progress.failedPhotos > 0) {
              <span class="text-red-300">({{ progress.failedPhotos }} failed)</span>
            }
          </div>
        </div>
        <i class="pi pi-times text-white ml-2 cursor-pointer" (click)="dismissCompleted()"></i>
      </div>
    </div>
    }
  `,
  styles: [`
    .upload-progress-badge {
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.2);
      z-index: 9999;
      min-width: 300px;
      animation: slideIn 0.3s ease-out;
    }

    .upload-progress-badge.completed {
      background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
      cursor: pointer;
    }

    .badge-content {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .upload-text {
      flex: 1;
    }

    @keyframes slideIn {
      from {
        transform: translateX(400px);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @keyframes slideOut {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(400px);
        opacity: 0;
      }
    }

    .upload-progress-badge.hiding {
      animation: slideOut 0.3s ease-out forwards;
    }
  `]
})
export class UploadProgressBadgeComponent implements OnInit, OnDestroy {
  progress: UploadProgress = {
    totalPhotos: 0,
    uploadedPhotos: 0,
    failedPhotos: 0,
    isUploading: false
  };
  showCompletedMessage = false;
  private subscription?: Subscription;
  private completedTimeout?: any;

  constructor(private photoUploadService: PhotoUploadService) {}

  ngOnInit() {
    this.subscription = this.photoUploadService.progress$.subscribe(progress => {
      const wasUploading = this.progress.isUploading;
      this.progress = progress;

      // Show completed message when upload finishes
      if (wasUploading && !progress.isUploading && progress.uploadedPhotos > 0) {
        this.showCompletedMessage = true;

        // Auto-dismiss after 5 seconds
        this.completedTimeout = setTimeout(() => {
          this.dismissCompleted();
        }, 5000);
      }
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.completedTimeout) {
      clearTimeout(this.completedTimeout);
    }
  }

  dismissCompleted() {
    this.showCompletedMessage = false;
    if (this.completedTimeout) {
      clearTimeout(this.completedTimeout);
    }
  }
}
